import { AfterViewInit, Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { LocationData, LocationService } from 'src/app/system/utility/location.service';
import { UIUtilityService } from '../../../system/utility/ui-utility.service';
import { ScreenOrientation, ScreenOrientationResult } from '@capacitor/screen-orientation';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { ModalController } from '@ionic/angular';
import { MEErrorService } from '../../error/me-error.service';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { SignatureCallbackRole, SignatureDataCallbackBean } from './singature-pad.service';
import SignaturePad from 'signature_pad';
import { logger } from '../../services/logging/logging.service';

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.less'],
    standalone: false
})
export class SignaturePadComponent implements OnInit, AfterViewInit {

  @ViewChild('canvas', { static: true }) signaturePadElement!: ElementRef;
  private signaturePad!: SignaturePad;

  protected UIUtilityService: any = UIUtilityService;

  private locationData!: LocationData;

  constructor(
    private elementRef: ElementRef,
    private locationService: LocationService,
    private modalCtrl: ModalController,
    private translate: TranslateUtilityService,
    private srvErr: MEErrorService,
  ) {

    if (!PlatformService.isMobile()) return;

    // https://github.com/szimek/signature_pad/issues/496
    // The only reliable way I've found of ensuring the canvas is rendered
    // before initialising the signature pad is to periodically check to
    // see whether it has any dimensions. Even then, for no obvious reason
    // it's not ready and we need to wait a bit longer. No event is reliably
    // fired after it's rendered and ready. The situation feels very hacky.
    ScreenOrientation.lock({ orientation: 'landscape' }).then(() => {
      const check = () => {
        if (this.signaturePadElement.nativeElement.width !== 0 && this.signaturePadElement.nativeElement.height !== 0) {
          setTimeout(() => {
            this.init();
          }, 200);
        } else {
          setTimeout(check, 20);
        }
      };
      setTimeout(check, 20);
    });

    // Prefetch the GPS Information
    this.locationService.getCurrentLocation().then(async (location: LocationData) => {
      this.locationData = location;
    });
  }

  ngOnInit() {
  }

  ngAfterViewInit(): void {
    this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement, { backgroundColor: 'rgba(255, 255, 255, 1)' }); 
    this.signaturePad.penColor = 'rgba(0, 0, 0, 1)';
    this.reset();
  }

  private init() {

    let displayWidth = window.innerWidth;
    let displayHeight = PlatformService.getHeight() - (PlatformService.isIOS() ? 80 : 90); // Try and error value

    const canvas: any = this.signaturePadElement.nativeElement;
    canvas.style.width = displayWidth+ "px";
    canvas.style.height = displayHeight + "px";

    let ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = displayWidth * ratio;
    canvas.height = displayHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    // For Debug only
    logger.debug("Canvas Width: " + canvas.width + " Height: " + canvas.height + " Ratio: " + ratio);

    this.reset();
  }

  private drawSignatureLine() {

    const canvas: any = this.elementRef.nativeElement.querySelector('canvas');

    // Base on the Orientation, we will draw the line
    ScreenOrientation.orientation().then((result: ScreenOrientationResult) => {

      const ctx = canvas.getContext("2d");

      // Create a Line for signature
      let ratio = Math.max(window.devicePixelRatio || 1, 1);
      let height = canvas.height / ratio;
      let width = canvas.width / ratio;

      let xMargin = 30;
      let yMargin = 20;
      let x1 = xMargin;
      let y1 = height - yMargin;
      let x2 = width - xMargin;
      let y2 = y1;

      ctx.lineWidth = 1;
      ctx.strokeStyle = "black";

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.closePath();
    });
  }

  public cancel() {
    let data: SignatureDataCallbackBean = {
      role: SignatureCallbackRole.CANCEL,
      latitude: null,
      longitude: null,
      base64Signature: null
    }
    this.dismiss(data);
  }

  public reset() {
    this.signaturePad.clear();
    this.drawSignatureLine();
  }

  public async save() {
    if (this.signaturePad.isEmpty()) {
      let title = this.translate.instant('ERROR_DIALOG.VALIDATION_ERROR');
      let errors = [this.translate.instant('ERROR_SIGNATURE_IS_REQUIRED')];
      this.srvErr.showDialog(title, errors);
      return;
    }

    // Prepare the signature parameter
    let base64Signature = await this.getSignature();
    let data: SignatureDataCallbackBean = {
      role: SignatureCallbackRole.CONFIRM,
      latitude: this.locationData?.latitude,
      longitude: this.locationData?.longitude,
      base64Signature: base64Signature
    };
    this.dismiss(data);
  }

  private async getSignature(): Promise<string> {

    // Save the current signature data
    const signatureOnlyData = this.signaturePad.toData();

    // 1. Clear the canvas and redraw only the user's strokes (no line)
    this.signaturePad.clear();
    this.signaturePad.fromData(signatureOnlyData);

    // 2. Export the image
    let result = await this.handleLandscape();

    // 3. Restore the signature line for user reference
    this.drawSignatureLine();

    // 4. Return the exported image data
    return result;
  }

  private dismiss(data: SignatureDataCallbackBean) {
    if (PlatformService.isMobile()) {
      ScreenOrientation.lock({ orientation: 'portrait' });
    }
    return this.modalCtrl.dismiss(data);
  }

  private resizeCanvas(canvas: HTMLCanvasElement, targetHeight: number): string {
    const originalWidth = canvas.width;
    const originalHeight = canvas.height;
    const targetWidth = Math.round((originalWidth / originalHeight) * targetHeight); // Calculate the Width based on the target finalHeight
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = targetWidth;
    finalCanvas.height = targetHeight;
    const finalCtx = finalCanvas.getContext('2d')!;
    finalCtx.fillStyle = '#fff';
    finalCtx.fillRect(0, 0, targetWidth, targetHeight);
    finalCtx.imageSmoothingEnabled = true;
    finalCtx.imageSmoothingQuality = 'high';
    finalCtx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
    return finalCanvas.toDataURL('image/png');
  }

  private async handleLandscape(): Promise<string> {

    const originalDataUrl = this.signaturePad.toDataURL("image/png");

    return await new Promise<string>((resolve) => {
      const img: HTMLImageElement = new Image();
      img.onload = () => {

        let canvas = this.signaturePadElement.nativeElement as HTMLCanvasElement;

        // Resize Canvas
        const targetHeight = 200;
        let result = this.resizeCanvas(canvas, targetHeight);
        resolve(result);
      };
      img.src = originalDataUrl;
    });
  }


}